/**
 * Strong lexicon entry loader for CONCORDANCE, OFFLINE-FIRST.
 *
 * Reads locally seeded entry (`strong_entry`, marked `localOnly`) by its
 * canonical Strong number (e.g. "H7225", "G3056"). Operates without network or server;
 * source for offline concordance MVP.
 */

import { eq, type PowerSyncDrizzleDB, strong_entry, type StrongEntry } from "@bso/powersync";

export type StrongEntryView = Pick<
  StrongEntry,
  "code" | "lemma" | "transliteration" | "pronunciation" | "gloss" | "definition" | "language"
>;

/**
 * Reads Strong entry from local DB. Returns `null` if code not
 * seeded (caller can fall back to Tauri/HTTP for unseeded lexicon).
 */
export async function loadStrongEntryFromLocal(
  db: PowerSyncDrizzleDB | null,
  code: string,
): Promise<StrongEntryView | null> {
  if (!db || !code) {
    return null;
  }
  const normalized = code.toUpperCase();
  const rows = await db
    .select({
      code: strong_entry.code,
      lemma: strong_entry.lemma,
      transliteration: strong_entry.transliteration,
      pronunciation: strong_entry.pronunciation,
      gloss: strong_entry.gloss,
      definition: strong_entry.definition,
      language: strong_entry.language,
    })
    .from(strong_entry)
    .where(eq(strong_entry.code, normalized))
    .limit(1);

  return rows[0] ?? null;
}
